import { Injectable, inject } from '@angular/core';
import { TourService, IStepOption } from 'ngx-ui-tour-md-menu';
import { AmplitudeService } from '../services/core/amplitude.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserDataService } from './core/user-data.service';

export const KEY_WELCOME_TOUR_SEEN = 'pantera_welcome_tour_seen';
export const KEY_WHATS_NEW_VERSION = 'pantera_whats_new_version';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService2 {
  private tourService = inject(TourService);
  translate = inject(TranslateService);
  private amplitude = inject(AmplitudeService);
  private user = inject(UserDataService);

  public version = environment.version;

  public loadedSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private selectedTabSubject = new BehaviorSubject<number>(0);
  public selectedTab$ = this.selectedTabSubject.asObservable();

  public shouldShowWelcomeTour(): boolean {
    return !localStorage.getItem(KEY_WELCOME_TOUR_SEEN);
  }

  public shouldShowWhatsNewTour(): boolean {
    return localStorage.getItem(KEY_WHATS_NEW_VERSION) !== this.version;
  }

  public markWelcomeTourAsSeen() {
    localStorage.setItem(KEY_WELCOME_TOUR_SEEN, 'true');
  }

  public markWhatsNewAsSeen() {
    localStorage.setItem(KEY_WHATS_NEW_VERSION, this.version);
  }

  // BehaviorSubjects for onboarding actions
  public manualDetectionBehaviorSubject = new BehaviorSubject(false);
  public selectFirstDetectionBehaviorSubject = new BehaviorSubject(false);
  public pointCameraToDetectionImagePTZBehaviorSubject = new BehaviorSubject(false);
  public openGeolocMenu = new BehaviorSubject(false);
  public enableAuxCamBehaviorSubject = new BehaviorSubject(false);
  public setAutomaticDetectionForEveryCameraBehaviorSubject = new BehaviorSubject(false);
  public openReclassDetMenu = new BehaviorSubject(false);
  public clearSelectedDetectionBehaviorSubject = new BehaviorSubject(false);

  public setLoaded(loaded: boolean): void {
    this.loadedSub.next(loaded);
  }

  public setSelectedTab(tabIndex: number) {
    this.selectedTabSubject.next(tabIndex);
  }

  startWelcomeTour() {
    this.startMapTourV2();
  }

  startWhatsNewTour() {
    this.user.sidePanelOpened.set(false);
    const steps: IStepOption[] = [
      {
        anchorId: 'welcome-step',
        title: this.translate.instant('TOUR.NEWS.TITLE'),
        content: this.translate.instant('TOUR.NEWS.CONTENT', {
          visualIcon: this.newsIcon('palette'),
          pdfIcon: this.newsIcon('picture_as_pdf'),
          supportIcon: this.newsIcon('support_agent'),
          riskIcon: this.newsIcon('schedule'),
        }),
        stepDimensions: { width: '650px', minWidth: '650px' },
        popoverClass: 'tour-center-step tour-news',
        enableBackdrop: true,
        backdropConfig: { offset: 20 },
        showArrow: false,
      },
    ];

    this.tourService.initialize(steps, {
      enableBackdrop: true,
      disablePageScrolling: true,
      popoverClass: 'tour-dark',
      prevBtnTitle: this.translate.instant('TOUR.BACK'),
      nextBtnTitle: this.translate.instant('TOUR.NEXT'),
      endBtnTitle: this.translate.instant('TOUR.CLOSE'),
    });

    this.tourService.start();

    this.tourService.end$.pipe(take(1)).subscribe(() => {
      this.markWhatsNewAsSeen();
    });

    this.amplitude.sendEvent('Iniciou Onboarding Novidades', { Versão: this.version });
  }

  private newsIcon(name: string): string {
    return `<span class='material-symbols-outlined tn-card-icon' aria-hidden='true'>${name}</span>`;
  }

  startMapTourV2() {
    this.tourService.end$.pipe(take(1)).subscribe(() => {
      this.markWelcomeTourAsSeen();
    });

    this.user.sidePanelOpened.set(false);

    const steps: IStepOption[] = [
      {
        anchorId: 'welcome-step',
        title: this.translate.instant('TOUR.ONBOARDING.WELCOME_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.WELCOME_CONTENT', {
          version: this.version,
        }),
        stepDimensions: { width: '450px', minWidth: '450px' },
        popoverClass: 'tour-center-step',
        enableBackdrop: true,
        backdropConfig: { offset: 20 },
        showArrow: false,
      },
      {
        anchorId: 'features-nav',
        title: this.translate.instant('TOUR.ONBOARDING.FEATURES_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.FEATURES_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'above' },
        enableBackdrop: true,
        popoverClass: 'tour-features-nav',
        showArrow: false,
      },
      {
        anchorId: 'layers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.MAP_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.MAP_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'above' },
        enableBackdrop: true,
        backdropConfig: { offset: 10 },
        popoverClass: 'tour-layers-btn',
        showArrow: false,
      },
      {
        anchorId: 'ocorrencias-btn',
        title: this.translate.instant('TOUR.ONBOARDING.FIRE_REPORTS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.FIRE_REPORTS_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'weather-btn',
        title: this.translate.instant('TOUR.ONBOARDING.WEATHER_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.WEATHER_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'cams-btn',
        title: this.translate.instant('TOUR.ONBOARDING.CAMERAS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.CAMERAS_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'satellites-btn',
        title: this.translate.instant('TOUR.ONBOARDING.SATELLITE_DETECTION_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.SATELLITE_DETECTION_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'brigades-btn',
        title: this.translate.instant('TOUR.ONBOARDING.RESOURCES_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.RESOURCES_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'historical-data-btn',
        title: this.translate.instant('TOUR.ONBOARDING.HISTORICAL_DATA_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.HISTORICAL_DATA_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'baseLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.BASE_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.BASE_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'clientRoiLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.TALHAO_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.TALHAO_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'reportLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.REPORT_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.REPORT_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        stepDimensions: { width: 'fit-content', minWidth: 'fit-content' },
        enableBackdrop: true,
      },
      {
        anchorId: 'riskLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.RISK_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.RISK_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        stepDimensions: { width: 'fit-content', minWidth: 'fit-content' },
        enableBackdrop: true,
      },
      {
        anchorId: 'visibilityLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.CAMERAS_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.CAMERAS_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        stepDimensions: { width: 'fit-content', minWidth: 'fit-content' },
        enableBackdrop: true,
      },
      {
        anchorId: 'satLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.SATELLITE_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.SATELLITE_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        stepDimensions: { width: 'fit-content', minWidth: 'fit-content' },
        enableBackdrop: true,
      },
      {
        anchorId: 'fmLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.RESOURCE_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.RESOURCE_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        stepDimensions: { width: 'fit-content', minWidth: 'fit-content' },
        enableBackdrop: true,
      },
      {
        anchorId: 'historicalDataLayers-btn',
        title: this.translate.instant('TOUR.ONBOARDING.HISTORICAL_DATA_LAYERS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.HISTORICAL_DATA_LAYERS_CONTENT'),
        placement: { xPosition: 'after', yPosition: 'below' },
        stepDimensions: { width: 'fit-content', minWidth: 'fit-content' },
        enableBackdrop: true,
      },
      {
        anchorId: 'links-step',
        title: this.translate.instant('TOUR.ONBOARDING.LINKS_TITLE'),
        content: this.translate.instant('TOUR.ONBOARDING.LINKS_CONTENT'),
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
        popoverClass: 'tour-center-step',
        backdropConfig: { offset: 20 },
        showArrow: false,
      },
    ];

    const filteredSteps = steps.filter((step) => {
      if (!step.anchorId) return true;

      return true;
    });

    // Initialize tour with steps
    this.tourService.initialize(filteredSteps, {
      enableBackdrop: true,
      disablePageScrolling: true,
      popoverClass: 'tour-dark',
      prevBtnTitle: this.translate.instant('TOUR.BACK'),
      nextBtnTitle: this.translate.instant('TOUR.NEXT'),
      endBtnTitle: this.translate.instant('TOUR.CLOSE'),
    });

    // Start the tour
    this.tourService.start();

    const openableLayerButtons = new Set([
      'baseLayers-btn',
      'clientRoiLayers-btn',
      'reportLayers-btn',
      'visibilityLayers-btn',
      'satLayers-btn',
      'fmLayers-btn',
      'riskLayers-btn',
      'historicalDataLayers-btn',
    ]);

    this.openAndCloseLayers(openableLayerButtons);

    // Track analytics
    this.amplitude.sendEvent('Iniciou Onboarding V2', { Versão: this.version });
  }

  startCamTourV2() {
    const steps: IStepOption[] = [
      {
        anchorId: 'cameras-list-wrapper',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_SELECTION')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_SELECTION_CONTENT')} ${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_SELECTION_LINK')}`,
        placement: { xPosition: 'after', yPosition: 'above' },
        enableBackdrop: true,
      },
      {
        anchorId: 'camera-wrapper',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.MOVING_CAMERA')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.MOVING_CAMERA_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
      },
      {
        anchorId: 'camera-action-buttons',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_CONTROLS')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_CONTROLS_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
        delayBeforeStepShow: 500,
      },
      {
        anchorId: 'cameras-controls-container',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_MODES')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.CAMERA_MODES_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'turn-on-all-detections-btn-wrapper',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.ENABLE_AUTO')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.CAMERA.ENABLE_AUTO_CONTENT')}`,
        placement: { xPosition: 'after', yPosition: 'above' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
      },
      {
        anchorId: 'links-step-interface',
        title: this.translate.instant('INTERFACE.ONBOARDING.CAMERA.LINKS_CAMERA_TITLE'),
        content: this.translate.instant('INTERFACE.ONBOARDING.CAMERA.LINKS_CAMERA_CONTENT'),
        placement: { xPosition: 'before', yPosition: 'below' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        popoverClass: 'tour-center-step',
        enableBackdrop: true,
        showArrow: false,
      },
    ];

    // Initialize tour with steps
    this.tourService.initialize(steps, {
      enableBackdrop: true,
      disablePageScrolling: true,
      prevBtnTitle: this.translate.instant('TOUR.BACK'),
      nextBtnTitle: this.translate.instant('TOUR.NEXT'),
      endBtnTitle: this.translate.instant('TOUR.CLOSE'),
    });

    // Start the tour
    this.tourService.start();

    const openableLayerButtons = new Set(['cameras-controls-btn']);

    this.openAndCloseLayers(openableLayerButtons);

    // Track analytics
    this.amplitude.sendEvent('Iniciou Onboarding de Câmeras V2', { Versão: this.version });
  }

  startDetTourV2(camerasAmount: number) {
    const steps: IStepOption[] = [
      {
        anchorId: 'detection-table-wrapper',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.DETECTIONS_LIST')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.DETECTIONS_LIST_CONTENT')} ${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.DETECTION_LIST_LINK')}`,
        placement: { xPosition: 'after', yPosition: 'above' },
        enableBackdrop: true,
        delayBeforeStepShow: 500,
      },
      {
        anchorId: 'camera-btn-manual-det',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.CREATE_MANUAL_DETECTION')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.CREATE_MANUAL_DETECTION_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
      },
      {
        anchorId: 'table-container',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.SELECT_DETECTION')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.SELECT_DETECTION_CONTENT')}`,
        placement: { xPosition: 'after', yPosition: 'above' },
        enableBackdrop: true,
      },
      {
        anchorId: 'verify-det-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.VIEW_DETECTION')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.VIEW_DETECTION_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
        delayBeforeStepShow: 500,
      },
      {
        anchorId: 'geoloc-det-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.GEOLOC')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.GEOLOC_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'above' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
        nextOnAnchorClick: true,
        isAsync: true,
        asyncStepTimeout: 2000,
        delayBeforeStepShow: 1500,
      },
    ];

    if (camerasAmount > 1) {
      steps.push({
        anchorId: 'enable-aux-cam-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.ENABLE_AUX_CAM')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.ENABLE_AUX_CAM_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
        popoverClass: 'tour-step-enable-aux',
      });
    }

    steps.push({
      anchorId: 'triangulate-btn',
      title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.TRIANGULATE')}`,
      content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.TRIANGULATE_CONTENT')}`,
      placement: { xPosition: 'before', yPosition: 'below' },
      enableBackdrop: true,
    });

    if (camerasAmount > 2) {
      steps.push({
        anchorId: 'select-second-cam-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.SWITCH_AUX_CAM')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.SWITCH_AUX_CAM_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'above' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
        asyncStepTimeout: 100,
        delayBeforeStepShow: 500,
      });
    }

    steps.push(
      {
        anchorId: 'map-click-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.INDICATE_FIRE_LOCATION')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.INDICATE_FIRE_LOCATION_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'above' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        delayBeforeStepShow: 800,
        enableBackdrop: true,
      },
      {
        anchorId: 'group-on-rep-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.GROUP_TO_REPORT')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.GROUP_TO_REPORT_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
      },
      {
        anchorId: 'create-report-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.CREATE_REPORT')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.CREATE_REPORT_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'below' },
        enableBackdrop: true,
      },
      {
        anchorId: 'delete-det-btn',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.DELETE_DETECTION')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.DELETE_DETECTION_CONTENT')}`,
        placement: { xPosition: 'before', yPosition: 'above' },
        enableBackdrop: true,
      },
      {
        anchorId: 'table-container',
        title: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.CANCEL_REPORT_PROCEDURE')}`,
        content: `${this.translate.instant('INTERFACE.ONBOARDING.DETECTION.CANCEL_REPORT_PROCEDURE_CONTENT')}`,
        placement: { xPosition: 'after', yPosition: 'above' },
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
      },
      {
        anchorId: 'links-step-interface',
        title: this.translate.instant('INTERFACE.ONBOARDING.DETECTION.LINKS_DETECTION_TITLE'),
        content: this.translate.instant('INTERFACE.ONBOARDING.DETECTION.LINKS_DETECTION_CONTENT'),
        stepDimensions: { width: '450px', minWidth: '450px' },
        enableBackdrop: true,
        popoverClass: 'tour-center-step',
        backdropConfig: { offset: 20 },
        showArrow: false,
      }
    );

    // Initialize tour with steps
    this.tourService.initialize(steps, {
      enableBackdrop: true,
      disablePageScrolling: true,
      prevBtnTitle: this.translate.instant('TOUR.BACK'),
      nextBtnTitle: this.translate.instant('TOUR.NEXT'),
      endBtnTitle: this.translate.instant('TOUR.CLOSE'),
    });

    // Start the tour
    this.tourService.start();

    this.setupDetectionTourActions();

    // Track analytics
    this.amplitude.sendEvent('Iniciou Onboarding de Detection V2', { Versão: this.version });
  }

  // configura as ações que devem ser executadas em cada step do tour de detecção
  private setupDetectionTourActions() {
    const actionMap = new Map<string, () => void>([
      [
        'camera-btn-manual-det',
        () => {
          this.manualDetectionBehaviorSubject.next(true);
        },
      ],
      [
        'table-container',
        () => {
          this.selectFirstDetectionBehaviorSubject.next(true);
        },
      ],
      [
        'verify-det-btn',
        async () => {
          this.pointCameraToDetectionImagePTZBehaviorSubject.next(true);
          this.openGeolocMenu.next(false);
        },
      ],
      [
        'geoloc-det-btn',
        async () => {
          this.openGeolocMenu.next(false);
          this.enableAuxCamBehaviorSubject.next(true);
        },
      ],
      [
        'triangulate-btn',
        () => {
          this.enableAuxCamBehaviorSubject.next(true);
        },
      ],
      [
        'select-second-cam-btn',
        () => {
          this.enableAuxCamBehaviorSubject.next(true);
        },
      ],
      [
        'map-click-btn',
        async () => {
          this.openGeolocMenu.next(true);
        },
      ],
      [
        'group-on-rep-btn',
        async () => {
          this.openGeolocMenu.next(true);
          this.enableAuxCamBehaviorSubject.next(true);
        },
      ],
      [
        'create-report-btn',
        () => {
          this.enableAuxCamBehaviorSubject.next(false);
          this.openReclassDetMenu.next(false);
        },
      ],
      [
        'delete-det-btn',
        () => {
          this.openReclassDetMenu.next(false);
        },
      ],
    ]);

    const subShow = this.tourService.stepShow$.subscribe(({ step }) => {
      const action = actionMap.get(step.anchorId || '');
      if (action) {
        setTimeout(() => action(), 50);
      }
    });

    const subEnd = this.tourService.end$.subscribe(() => {
      this.cleanUpTourState(subShow, subEnd, null, true);
    });
  }

  // executa ações ao abrir/fechar layers durante o tour
  private openAndCloseLayers(openableLayerButtons: Set<string>) {
    const isSelected = (id: string) => {
      const el = document.getElementById(id);
      return !!el && el.classList.contains('selected-ctrl-btn');
    };
    const toggle = (id: string) => document.getElementById(id)?.click();
    const closeIfOpen = (id: string) => {
      if (isSelected(id)) toggle(id);
    };

    if (openableLayerButtons.has('cameras-controls-btn')) {
      setTimeout(() => {
        if (!isSelected('cameras-controls-btn')) toggle('cameras-controls-btn');
      }, 100);
    }

    const subShow = this.tourService.stepShow$.subscribe(({ step }) => {
      if (step.anchorId && openableLayerButtons.has(step.anchorId)) {
        setTimeout(() => {
          if (!isSelected(step.anchorId!)) toggle(step.anchorId!);
        }, 50);
      } else {
        setTimeout(() => closeIfOpen('historicalDataLayers-btn'), 0);
      }
    });

    const subHide = this.tourService.stepHide$.subscribe(({ step }) => {
      if (step.anchorId === 'historicalDataLayers-btn') {
        setTimeout(() => closeIfOpen('historicalDataLayers-btn'), 0);
      }
    });

    const subEnd = this.tourService.end$.subscribe(() => {
      this.cleanUpTourState(subShow, subEnd, subHide);
    });
  }

  // limpa os estados alterados no tour
  private cleanUpTourState(
    subShow: Subscription,
    subEnd: Subscription,
    subHide?: Subscription | null,
    clearDet = false
  ) {
    if (subShow) subShow.unsubscribe();
    if (subEnd) subEnd.unsubscribe();
    if (subHide) subHide.unsubscribe();

    if (clearDet) {
      this.clearSelectedDetectionBehaviorSubject.next(true);

      this.manualDetectionBehaviorSubject.next(false);
      this.selectFirstDetectionBehaviorSubject.next(false);
      this.openGeolocMenu.next(false);
      this.enableAuxCamBehaviorSubject.next(false);
      this.openReclassDetMenu.next(false);
      this.clearSelectedDetectionBehaviorSubject.next(false);
    }
  }

  // Método para testar individualmente
  testTour() {
    this.startMapTourV2();
  }
}
